/**
 * Wave chat-pills · 2026-05-21 — ChatMinimizedPills
 *
 * Pills persistentes (Messenger-style chat heads) ancoradas bottom-right.
 * SEPARADOS visualmente do ChatNotificationDock:
 *
 *   ChatNotificationDock = EPHEMERAL — cards ~280px de largura com preview
 *     completo, somem em 8s. Anchor: bottom-3 / bottom-20 quando tem
 *     pill embaixo.
 *
 *   ChatMinimizedPills  = PERSISTENT — pills compactas ~140px (avatar +
 *     name + badge unread), vivem por 30min de TTL ou até user fechar.
 *     Anchor: bottom-3.
 *
 * Lifecycle:
 *   - Auto-pin: pinConversation() é chamado no send-success (ChatPanel)
 *     e no detect de msg nova (ChatNotificationDock).
 *   - Auto-prune: setInterval 60s → pruneInactivePills() remove silently
 *     entries que passaram da TTL (30min sem atividade).
 *   - Manual close: X da pill → unpinConversation(id, manual=true). Não
 *     ressuscita até nova atividade genuína.
 *
 * Click pill → openThreadInPanel(id) (já zera o unread via markPillRead
 * no ChatPanel ThreadView).
 *
 * Overflow: 3 pills visíveis. 4+ vira chip "+N outras" que abre o panel
 * na lista.
 *
 * Z-index: z-[55] — mesmo do dock; mas anchors diferentes evitam overlap.
 */

import { useEffect, useMemo } from 'react';
import { useAuth } from '@/store/auth.store';
import {
  useChatInstantStore,
  selectActivePills,
  selectPillOverflowCount,
  type ChatPillEntry,
} from '@/store/chat-instant.store';
import type { ChatInstantConversation } from '@/api/chat-instant';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';

const PRUNE_TICK_MS = 60_000;

export function ChatMinimizedPills() {
  const { t, locale } = useT();
  const isEn = locale === 'en';
  const me = useAuth((s) => s.user);
  const pills = useChatInstantStore(selectActivePills);
  const overflow = useChatInstantStore(selectPillOverflowCount);
  const conversations = useChatInstantStore((s) => s.conversations);
  const openPanel = useChatInstantStore((s) => s.openPanel);
  const openThreadInPanel = useChatInstantStore((s) => s.openThreadInPanel);
  const unpinConversation = useChatInstantStore((s) => s.unpinConversation);
  const pruneInactivePills = useChatInstantStore((s) => s.pruneInactivePills);

  // Cleanup tick — uma única instância global registrada quando a feature
  // monta. Idempotente: se o componente desmonta, limpa o interval.
  useEffect(() => {
    if (!me) return;
    // Run uma vez imediatamente para limpar resíduos de sessão anterior
    // (caso reload tenha persistido — hoje não persiste, mas defensivo).
    pruneInactivePills();
    const id = window.setInterval(pruneInactivePills, PRUNE_TICK_MS);
    return () => window.clearInterval(id);
  }, [me, pruneInactivePills]);

  // Index conversations por id pra lookup O(1) na hora de renderizar.
  const convsById = useMemo(() => {
    const m = new Map<string, ChatInstantConversation>();
    for (const c of conversations) m.set(c.id, c);
    return m;
  }, [conversations]);

  if (!me) return null;
  if (pills.length === 0 && overflow === 0) return null;

  const handleOpenPill = (convId: string) => {
    openThreadInPanel(convId);
  };

  const handleDismissPill = (e: React.MouseEvent, convId: string) => {
    e.stopPropagation();
    unpinConversation(convId, true);
  };

  const handleOpenOverflow = () => {
    openPanel();
  };

  return (
    <div
      data-testid="chat-instant-minimized-pills"
      aria-label={t.chatInstant.pills.label}
      className={cn(
        // Bottom-right, ABAIXO do dock de notificações (que ancora em
        // bottom-3 com cards mais altos) — pra evitar overlap visual, as
        // pills colam BEM no canto e o dock se levanta. Ambos z-[55].
        // Mobile: respeita safe area + MobileBottomNav (~72px).
        'fixed z-[55] flex flex-row-reverse items-center gap-2',
        // Desktop: bottom-3 right-3 (mesmo nível do dock, mas dock empilha
        // verticalmente acima quando ambos visíveis — pills são UMA linha).
        'right-3 bottom-3',
        'md:bottom-3 md:right-3',
        // Mobile: empurra acima do MobileBottomNav (h ~60px) + safe area.
        'max-md:bottom-[calc(env(safe-area-inset-bottom)+72px)]',
        'max-md:right-3',
        // Eventos só nas pills — fundo continua clicável.
        'pointer-events-none',
      )}
    >
      <style>{`
        @keyframes chatPillIn {
          0% { transform: translateY(8px) scale(0.8); opacity: 0; }
          100% { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes chatPillBadgePulse {
          0% { transform: scale(0.6); opacity: 0; }
          60% { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>

      {pills.map((pill) => {
        const conv = convsById.get(pill.conversationId);
        return (
          <PillCard
            key={pill.conversationId}
            pill={pill}
            conv={conv}
            meId={me.id}
            isEn={isEn}
            onOpen={() => handleOpenPill(pill.conversationId)}
            onDismiss={(e) => handleDismissPill(e, pill.conversationId)}
            dismissLabel={t.chatInstant.pills.dismissPin}
            openLabel={
              conv
                ? t.chatInstant.pills.openWith(peerNameOf(conv, me.id, isEn))
                : t.chatInstant.pills.openGeneric
            }
          />
        );
      })}

      {overflow > 0 && (
        <button
          type="button"
          onClick={handleOpenOverflow}
          aria-label={t.chatInstant.pills.moreCount(overflow)}
          data-testid="chat-instant-minimized-pills-overflow"
          className={cn(
            'pointer-events-auto pressable focus-ring',
            'inline-flex h-11 items-center justify-center rounded-full',
            'border border-line bg-surface-2 px-3 text-[11px] font-medium text-ink-muted',
            'shadow-pop dark:shadow-pop-dark',
            'hover:bg-surface-3 hover:text-ink transition-colors duration-150',
            'animate-[chatPillIn_240ms_cubic-bezier(0.32,0.72,0,1)_both]',
          )}
        >
          {t.chatInstant.pills.moreCount(overflow)}
        </button>
      )}
    </div>
  );
}

// ── Pill individual ──────────────────────────────────────────────────

function PillCard({
  pill,
  conv,
  meId,
  isEn,
  onOpen,
  onDismiss,
  dismissLabel,
  openLabel,
}: {
  pill: ChatPillEntry;
  conv: ChatInstantConversation | undefined;
  meId: string;
  isEn: boolean;
  onOpen: () => void;
  onDismiss: (e: React.MouseEvent) => void;
  dismissLabel: string;
  openLabel: string;
}) {
  const name = conv ? peerNameOf(conv, meId, isEn) : '…';
  const avatarUrl =
    conv?.type === 'dm'
      ? conv.members.find((m) => m.userId !== meId)?.avatarUrl ?? null
      : null;
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  const unread = pill.unreadCount;

  return (
    <div
      data-testid="chat-instant-minimized-pill"
      data-conv-id={pill.conversationId}
      className={cn(
        'pointer-events-auto group flex items-center gap-1.5',
        // Pill compacta — 44px tall, ~140px wide. Avatar 32 + name + X.
        'h-11 max-w-[160px] rounded-full border border-line bg-surface-2',
        'pl-1 pr-1.5 py-1',
        'shadow-pop dark:shadow-pop-dark',
        'animate-[chatPillIn_240ms_cubic-bezier(0.32,0.72,0,1)_both]',
        // Hover lift sutil.
        'transition-transform duration-150 ease-out',
        'hover:scale-[1.02] active:scale-[0.97]',
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label={openLabel}
        className={cn(
          'focus-ring flex flex-1 min-w-0 items-center gap-2 rounded-full text-left',
          'pl-0.5 pr-1',
        )}
      >
        {/* Avatar 32px + unread badge no canto. */}
        <div className="relative shrink-0">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={name}
              className="h-8 w-8 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-500/15 text-[10px] font-semibold text-brand-600">
              {initials || '?'}
            </div>
          )}
          {unread > 0 && (
            <span
              data-testid="chat-instant-minimized-pill-badge"
              className={cn(
                'absolute -bottom-0.5 -right-0.5 inline-flex h-[14px] min-w-[14px]',
                'items-center justify-center rounded-full bg-brand-500 px-[3px]',
                'text-[9px] font-semibold leading-none text-white ring-2 ring-surface-2',
                'animate-[chatPillBadgePulse_240ms_cubic-bezier(0.32,0.72,0,1)_both]',
              )}
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </div>
        <span
          className={cn(
            'truncate text-xs leading-tight',
            unread > 0 ? 'font-semibold text-ink' : 'text-ink',
          )}
        >
          {name}
        </span>
      </button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={dismissLabel}
        data-testid="chat-instant-minimized-pill-dismiss"
        className={cn(
          'focus-ring shrink-0 flex h-8 w-8 items-center justify-center rounded-full',
          'text-ink-soft hover:bg-surface-3 hover:text-ink transition-colors duration-120',
        )}
      >
        <svg className="h-3 w-3" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M4 4l8 8M12 4l-8 8"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function peerNameOf(conv: ChatInstantConversation, meId: string, isEn: boolean): string {
  if (conv.type === 'group') return conv.name ?? (isEn ? 'Group' : 'Grupo');
  const peer = conv.members.find((m) => m.userId !== meId);
  return peer?.fullName ?? (isEn ? '(you)' : '(você)');
}
